// Applies the active brand to the document as CSS custom properties, and loads
// the brand's webfonts. Called once at boot from src/index.js, before render.
//
// Solid colors become `--color-<kebab>` holding an "R G B" triplet, so Tailwind
// can do `rgb(var(--color-x) / <alpha-value>)` and keep opacity modifiers.
// Overlays/gradients are emitted verbatim as `--overlay-*` / `--gradient-*`.

import { activeBrand } from "./index";

const camelToKebab = (s) =>
  s
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2") // camelCase boundary
    .replace(/([a-zA-Z])([0-9])/g, "$1-$2") // letter→digit (neutral500 → neutral-500)
    .toLowerCase();

// "#007fff" | "#fff" -> "0 127 255"
export const hexToRgbTriplet = (hex) => {
  let h = String(hex).trim().replace(/^#/, "");
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const int = parseInt(h.slice(0, 6), 16);
  // eslint-disable-next-line no-bitwise
  const r = (int >> 16) & 255;
  // eslint-disable-next-line no-bitwise
  const g = (int >> 8) & 255;
  // eslint-disable-next-line no-bitwise
  const b = int & 255;
  return `${r} ${g} ${b}`;
};

// Same vars as applyBrandCssVariables, but as a plain object so they can be
// spread onto a React `style` prop. Needed by print/PDF templates: react-to-print
// clones content into a hidden iframe that copies <style>/<link> tags but not the
// inline custom properties JS sets on the live document's <html> element, so
// those clones would otherwise fall back to global.css's static (revivo) values.
export function getBrandCssVariables(brand = activeBrand) {
  const vars = {};

  Object.entries(brand.colors).forEach(([key, value]) => {
    vars[`--color-${camelToKebab(key)}`] = hexToRgbTriplet(value);
  });

  Object.entries(brand.overlays || {}).forEach(([key, value]) => {
    vars[`--overlay-${camelToKebab(key)}`] = value;
  });

  Object.entries(brand.gradients || {}).forEach(([key, value]) => {
    vars[`--gradient-${camelToKebab(key)}`] = value;
  });

  vars["--font-body"] = brand.fonts.body.stack;
  vars["--font-heading"] = brand.fonts.heading.stack;

  return vars;
}

// Precomputed for the active brand — brand is fixed for the lifetime of the
// page load (selected via REACT_APP_BRAND at build/start time).
export const brandCssVariables = getBrandCssVariables();

export function applyBrandCssVariables(brand = activeBrand, target) {
  const root = target || document.documentElement;
  const vars = brand === activeBrand ? brandCssVariables : getBrandCssVariables(brand);

  Object.entries(vars).forEach(([prop, value]) => {
    root.style.setProperty(prop, value);
  });
}

export function loadBrandFonts(brand = activeBrand, doc) {
  const d = doc || document;
  const urls = [
    ...new Set(
      Object.values(brand.fonts)
        .map((f) => f && f.url)
        .filter(Boolean)
    ),
  ];

  urls.forEach((url) => {
    if (d.querySelector(`link[data-brand-font][href="${url}"]`)) return;
    const link = d.createElement("link");
    link.rel = "stylesheet";
    link.href = url;
    link.setAttribute("data-brand-font", "");
    d.head.appendChild(link);
  });
}

export function applyBrand(brand = activeBrand, target) {
  applyBrandCssVariables(brand, target);
  loadBrandFonts(brand);
}

export default applyBrand;
